import { ChronoUnit, DateTimeFormatter, LocalDateTime } from "@js-joda/core";
import { DashboardTabModel, DateLoadConfig } from "api/model/dashboard/DashboardTab";
import { Dashboard, TabGroup } from "api/model/dashboard/Dashboard";
import { setDashboard } from "store/dashboard-store";
import { alignTo } from "components/dashboard/product-template-table/utils/timeUtils";
import { TreeNode } from "primereact/treenode";
import { MenuItem } from "primereact/menuitem";
import { I, T } from "services/lang/LanguageService";

const normalizeChronoUnit = (unit: any) => {
  if (typeof unit === "object") {
    const { _name: name } = unit;
    return ChronoUnit[name?.toUpperCase() as keyof typeof ChronoUnit] || null;
  }
  const resolvedUnit = ChronoUnit[unit?.toString().toUpperCase() as keyof typeof ChronoUnit];
  return resolvedUnit || null;
};

const adjustReferenceTime = (loadConfig: DateLoadConfig = {}) => {
  let referenceTime = LocalDateTime.now();
  let dateFrom = referenceTime;
  let dateTo = referenceTime;

  if (loadConfig.alignStartTo) {
    const alignUnit = normalizeChronoUnit(loadConfig.alignStartTo);
    if (alignUnit) {
      referenceTime = alignTo(referenceTime, alignUnit);
    }
  }
  
  if (loadConfig.timeOffsetUnit) {
    const timeOffsetUnit = normalizeChronoUnit(loadConfig.timeOffsetUnit);
    if (loadConfig.timeOffset != null && timeOffsetUnit) {
      referenceTime = referenceTime.plus(loadConfig.timeOffset, timeOffsetUnit);
    }
  }
  if (loadConfig.lagUnit) {
    const lagUnit = normalizeChronoUnit(loadConfig.lagUnit);
    if (loadConfig.lag != null && lagUnit) {
      dateFrom = referenceTime.minus(loadConfig.lag, lagUnit);
    }
  }

  if (loadConfig.leadUnit) {
    const leadUnit = normalizeChronoUnit(loadConfig.leadUnit);
    if (loadConfig.lead != null && leadUnit) {
      dateTo = referenceTime.plus(loadConfig.lead, leadUnit);
    }
  }

  return {
    referenceTime: referenceTime.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME).toString(),
    dateFrom:
      loadConfig.lag != null && loadConfig.lead != null
        ? dateFrom.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME)
        : undefined,
    dateTo:
      loadConfig.lag != null && loadConfig.lead != null
        ? dateTo.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME).toString()
        : undefined,
  };
};

const onDragEndSplice = <T>(result: any, itemsToReorder: T[]): T[] => {
  const reorderedItems = Array.from(itemsToReorder);
  const [removed] = reorderedItems.splice(result.source.index, 1);
  reorderedItems.splice(result.destination.index, 0, removed);

  return reorderedItems;
};

const updateDashboardTabs = (dashboard: Dashboard, newTabs: Array<DashboardTabModel>) => {
  const updatedDashboard = {
    ...dashboard,
    dashboardTabsJs: newTabs,
  } as Dashboard;
  setDashboard(updatedDashboard);

  return updatedDashboard;
};

const tabUpdateInDashboard = (
  dashboardTabs: any[],
  currentTabIndex: number,
  updatedTabContent: any
) => {
  return [
    ...dashboardTabs.slice(0, currentTabIndex),
    {
      ...dashboardTabs[currentTabIndex],
      ...updatedTabContent,
    },
    ...dashboardTabs.slice(currentTabIndex + 1),
  ];
};

const getFlatNodeMap = (treeData: TreeNode[]): Record<string, TreeNode> => {
  const map: Record<string, TreeNode> = {};

  const recurse = (nodes: TreeNode[]) => {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      map[node.key as string] = node;
      if (node.children) recurse(node.children);
    }
  };

  recurse(treeData);
  return map;
};

const getMenuItems = (
  tabs: DashboardTabModel[],
  tabGroups: TabGroup[],
  onTabChange: (uuid: string) => void
): MenuItem[] => {
  if (tabGroups.length === 0) {
    return tabs.map(tab => ({
      label: tab.title,
      command: () => onTabChange(tab.uuid),
    }));
  }

  const menuItems: MenuItem[] = tabGroups.map(group => ({
    label: group.title,
    items: tabs
      .filter(tab => tab.tabGroupId === group.uuid)
      .map(tab => ({
        label: `\u00A0\u00A0 ${tab.title}`,
        command: () => onTabChange(tab.uuid),
      })),
  }));

  const ungroupedTabs = tabs.filter(tab => !tab.tabGroupId);
  if (ungroupedTabs.length > 0) {
    menuItems.push({
      label: T("label.tabs-without-group"),
      items: ungroupedTabs.map(tab => ({
        label: `\u00A0\u00A0 ${tab.title}`,
        command: () => onTabChange(tab.uuid),
      })),
    });
  }

  return menuItems;
};

const getTreeSelectOptions = (tabs: DashboardTabModel[], tabGroups: TabGroup[] = []) => {
  if (tabGroups.length === 0) {
    return tabs.map(tab => ({
      key: tab.uuid,
      label: I(tab.title),
      data: tab,
    }));
  }

  const grouped = tabGroups.map(group => ({
    key: group.uuid,
    label: I(group.title),
    children: tabs
      .filter(tab => tab.tabGroupId === group.uuid)
      .map(tab => ({
        key: tab.uuid,
        label: I(tab.title),
        data: tab,
      })),
  }));

  const ungroupedTabs = tabs.filter(tab => !tab.tabGroupId);
  if (ungroupedTabs.length > 0) {
    grouped.push({
      key: "ungrouped",
      label: T("label.tabs-without-group"),
      children: ungroupedTabs.map(tab => ({
        key: tab.uuid,
        label: I(tab.title),
        data: tab,
      })),
    });
  }

  return grouped;
};

export { onDragEndSplice, updateDashboardTabs, tabUpdateInDashboard, adjustReferenceTime, getFlatNodeMap, getMenuItems, getTreeSelectOptions };
